// components/DonutChartCard.tsx

import React, { useRef, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import ContentLoader from 'react-content-loader';

import { Tooltip as TooltipComponent } from '../../../components';
import {
  calculateStatisticsPercentage,
  formatStatisticValue,
} from '../../../helpers/displayDataHelpers';
import { ArrowDownRightRedIcon, ArrowUpRightGreenIcon } from '../../../assets';
import { DonutChartCardProps } from './dashboardModels';

Chart.register(ArcElement, Tooltip, Legend);

const DonutChartCard: React.FC<DonutChartCardProps> = ({
  isLoading,
  value,
  prevValue,
  title,
  label,
  tooltipText,
  colors,
  textColor,
  filterTitle,
}) => {
  const chartRef = useRef<any>(null);
  const remaining = value === 0 ? 1 : value;

  const data = {
    labels: ['Main'],
    datasets: [
      {
        data: [value, remaining],
        backgroundColor: [colors[0], colors[1]],
        hoverOffset: 0,
        spacing: 0,
        cutout: '80%',
        borderRadius: 50,
      },
    ],
  };

  const options = {
    rotation: 180,
    circumference: 360,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const ctx = chartInstance.ctx;
      const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
      const gradientValue = Math.min(Math.max(value / 100, 0), 1);

      gradient.addColorStop(0, colors[0]);
      gradient.addColorStop(gradientValue, colors[1]);

      chartInstance.data.datasets[0].backgroundColor = [
        gradient,
        `${colors[0]}0F`,
      ];
      chartInstance.update();
    }
  }, [value, colors, isLoading]);

  const isPositive = value >= prevValue;
  const percentage = calculateStatisticsPercentage(value, prevValue);

  return (
    <div className="donut-chart">
      <div className="donut-chart__circle-container">
        {isLoading ? null : (
          <Doughnut data={data} options={options} ref={chartRef} />
        )}
        <div className="donut-chart__center-text">
          {isLoading ? (
            <div className="donut-chart__loader-text">
              <ContentLoader
                speed={1.5}
                width={25}
                height={30}
                viewBox="0 0 25 30"
                backgroundColor="#efefef"
                foregroundColor="#dedede"
              >
                <rect x="0" y="0" rx="5" ry="5" width="25" height="30" />
              </ContentLoader>
            </div>
          ) : (
            <span className={`donut-chart__value ${textColor}`}>
              {formatStatisticValue(value)}
            </span>
          )}
          <span className="donut-chart__label">{label}</span>
        </div>
      </div>
      <div className="donut-chart__info">
        <div className="donut-chart__header">
          <h4 className="donut-chart__title">{title}</h4>
          <div className="donut-chart__tooltip">
            <TooltipComponent tooltipText={tooltipText} tooltipWidth={250} />
          </div>
        </div>
        {isLoading ? (
          <div className="donut-chart__loader">
            <ContentLoader
              speed={1.5}
              width={150}
              height={24}
              viewBox="0 0 150 24"
              backgroundColor="#efefef"
              foregroundColor="#dedede"
            >
              <rect x="0" y="0" rx="5" ry="5" width="150" height="24" />
            </ContentLoader>
          </div>
        ) : (
          <div className="donut-chart__trend">
            <div className="donut-chart__icon">
              {isPositive ? (
                <ArrowUpRightGreenIcon />
              ) : (
                <ArrowDownRightRedIcon />
              )}
            </div>
            <span
              className={`donut-chart__percentage ${isPositive ? 'donut-chart__positive' : 'donut-chart__negative'}`}
            >
              {isPositive ? '+' : ''}
              {percentage}
            </span>
            <span className="donut-chart__vs">{`vs ${filterTitle}`}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonutChartCard;
